import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import CheckIcon from '@material-ui/icons/Check';
import DeleteIcon from '@material-ui/icons/Delete';
import BuildIcon from '@material-ui/icons/Build';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import { AuthContext } from '../../App';

const TYPES = [
  { value: 'oil_change', label: 'Mudança de Óleo' },
  { value: 'tires', label: 'Pneus' },
  { value: 'revision', label: 'Revisão Geral' },
  { value: 'ipo', label: 'IPO / Inspeção' },
  { value: 'insurance', label: 'Seguro' },
  { value: 'tachograph', label: 'Tacógrafo (calibração)' },
  { value: 'brake_check', label: 'Travões' },
  { value: 'filters', label: 'Filtros' },
  { value: 'battery', label: 'Bateria' },
  { value: 'other', label: 'Outro' },
];

const SEV_COLORS = { critical: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };
const SEV_BG = { critical: '#fee2e2', warning: '#fef3c7', info: '#dbeafe' };

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
    backdropFilter: 'blur(8px)', zIndex: 1300,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  dialog: {
    background: '#fff', borderRadius: 16, width: 600, maxWidth: '95vw',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', background: 'linear-gradient(180deg, #fff 0%, #fef3c7 100%)',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  content: { flex: 1, overflowY: 'auto', padding: '12px 20px' },
  card: {
    border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 8,
    display: 'flex', alignItems: 'center', gap: 10,
    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  },
}));

function MaintenanceDialog({ open, onClose, lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [records, setRecords] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ vehicleId: '', type: 'oil_change', lastDoneAt: '', lastDoneKm: '', intervalKm: '', intervalDays: '', cost: '', notes: '' });

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  async function fetchData() {
    if (!token) return;
    const [rRes, aRes, vRes] = await Promise.all([
      fetch('/planner-api/maintenance', { headers }),
      fetch('/planner-api/maintenance/alerts', { headers }),
      fetch('/planner-api/vehicles', { headers }),
    ]);
    setRecords(await rRes.json());
    setAlerts(await aRes.json());
    setVehicles(await vRes.json());
  }

  useEffect(() => { if (open) fetchData(); }, [open, token]);

  async function handleCreate() {
    if (!form.vehicleId || !form.type) return;
    await fetch('/planner-api/maintenance', { method: 'POST', headers, body: JSON.stringify(form) });
    setShowForm(false);
    setForm({ vehicleId: '', type: 'oil_change', lastDoneAt: '', lastDoneKm: '', intervalKm: '', intervalDays: '', cost: '', notes: '' });
    fetchData();
  }

  async function handleComplete(id) {
    await fetch(`/planner-api/maintenance/${id}/complete`, { method: 'PATCH', headers, body: JSON.stringify({}) });
    fetchData();
  }

  async function handleDelete(id) {
    await fetch(`/planner-api/maintenance/${id}`, { method: 'DELETE', headers });
    fetchData();
  }

  if (!open) return null;

  const typeLabel = (t) => TYPES.find(x => x.value === t)?.label || t;

  return (
    <Box className={classes.overlay} onClick={onClose}>
      <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
        <Box className={classes.header}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <BuildIcon style={{ fontSize: 20, color: '#f59e0b' }} />
            <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
              {pt ? 'Manutenção Preventiva' : 'Preventive Maintenance'}
            </Typography>
            <Chip label={records.length} size="small"
              style={{ height: 20, fontSize: '0.6rem', fontWeight: 700, background: '#fef3c7', color: '#f59e0b' }} />
          </Box>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 14 }} />}
            style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 12px' }}>
            {pt ? 'Fechar' : 'Close'}
          </Button>
        </Box>

        <Box className={classes.content}>
          {/* Alerts */}
          {alerts.length > 0 && (
            <Box style={{ marginBottom: 16 }}>
              <Typography style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase' }}>
                {pt ? 'Alertas' : 'Alerts'} ({alerts.length})
              </Typography>
              {alerts.slice(0, 5).map((a, i) => (
                <Box key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8,
                  background: SEV_BG[a.severity], marginBottom: 4,
                }}>
                  {a.severity === 'critical' ? <ErrorIcon style={{ fontSize: 14, color: SEV_COLORS[a.severity] }} /> :
                    <WarningIcon style={{ fontSize: 14, color: SEV_COLORS[a.severity] }} />}
                  <Typography style={{ fontSize: '0.72rem', fontWeight: 600, color: SEV_COLORS[a.severity], flex: 1 }}>
                    {a.message}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {/* Add button */}
          <Button size="small" variant="contained" onClick={() => setShowForm(!showForm)}
            startIcon={<AddIcon style={{ fontSize: 14 }} />}
            style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#f59e0b', color: '#fff', marginBottom: 12 }}>
            {pt ? 'Adicionar Manutenção' : 'Add Maintenance'}
          </Button>

          {/* Form */}
          {showForm && (
            <Box style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fafbfc' }}>
              <Box style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                  <InputLabel style={{ fontSize: '0.72rem' }}>{pt ? 'Veículo' : 'Vehicle'}</InputLabel>
                  <Select value={form.vehicleId} onChange={e => setForm({ ...form, vehicleId: e.target.value })}
                    label={pt ? 'Veículo' : 'Vehicle'} style={{ fontSize: '0.72rem' }}>
                    {vehicles.map(v => <MenuItem key={v.id} value={v.id} style={{ fontSize: '0.72rem' }}>{v.plate}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                  <InputLabel style={{ fontSize: '0.72rem' }}>{pt ? 'Tipo' : 'Type'}</InputLabel>
                  <Select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                    label={pt ? 'Tipo' : 'Type'} style={{ fontSize: '0.72rem' }}>
                    {TYPES.map(t => <MenuItem key={t.value} value={t.value} style={{ fontSize: '0.72rem' }}>{t.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Box>
              <Box style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <TextField size="small" variant="outlined" type="date" label={pt ? 'Última vez' : 'Last done'}
                  InputLabelProps={{ shrink: true }} value={form.lastDoneAt}
                  onChange={e => setForm({ ...form, lastDoneAt: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' } }} />
                <TextField size="small" variant="outlined" type="number" label={pt ? 'Km' : 'Km'}
                  value={form.lastDoneKm} onChange={e => setForm({ ...form, lastDoneKm: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' } }} />
              </Box>
              <Box style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <TextField size="small" variant="outlined" type="number" label={pt ? 'Intervalo km' : 'Interval km'}
                  value={form.intervalKm} onChange={e => setForm({ ...form, intervalKm: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' } }} />
                <TextField size="small" variant="outlined" type="number" label={pt ? 'Intervalo dias' : 'Interval days'}
                  value={form.intervalDays} onChange={e => setForm({ ...form, intervalDays: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' } }} />
                <TextField size="small" variant="outlined" type="number" label={pt ? 'Custo €' : 'Cost €'}
                  value={form.cost} onChange={e => setForm({ ...form, cost: e.target.value })}
                  style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.72rem' } }} />
              </Box>
              <Box style={{ display: 'flex', gap: 8 }}>
                <Button size="small" variant="contained" onClick={handleCreate}
                  style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#22c55e', color: '#fff' }}>
                  {pt ? 'Guardar' : 'Save'}
                </Button>
                <Button size="small" onClick={() => setShowForm(false)}
                  style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, color: '#64748b' }}>
                  {pt ? 'Cancelar' : 'Cancel'}
                </Button>
              </Box>
            </Box>
          )}

          {/* Records list */}
          {records.map(r => {
            const isOverdue = r.nextDueAt && new Date(r.nextDueAt) < new Date();
            const borderColor = isOverdue ? '#ef4444' : '#e2e8f0';
            return (
              <Box key={r.id} className={classes.card} style={{ borderColor }}>
                <Box style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: isOverdue ? '#ef4444' : '#22c55e',
                }} />
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1e293b' }}>
                    {typeLabel(r.type)} — {r.vehicle?.plate || '?'}
                  </Typography>
                  <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                    {r.nextDueAt ? `Próx: ${new Date(r.nextDueAt).toLocaleDateString('pt-PT')}` : ''}
                    {r.nextDueKm ? ` · ${r.nextDueKm} km` : ''}
                    {r.cost ? ` · ${Number(r.cost).toFixed(0)}€` : ''}
                  </Typography>
                </Box>
                {isOverdue && <Chip label={pt ? 'Vencido' : 'Overdue'} size="small"
                  style={{ height: 18, fontSize: '0.55rem', fontWeight: 700, background: '#fee2e2', color: '#ef4444' }} />}
                <IconButton size="small" onClick={() => handleComplete(r.id)} title={pt ? 'Marcar como feita' : 'Mark as done'}
                  style={{ color: '#22c55e' }}>
                  <CheckIcon style={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="small" onClick={() => handleDelete(r.id)} style={{ color: '#ef4444' }}>
                  <DeleteIcon style={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}

          {records.length === 0 && (
            <Box style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
              <BuildIcon style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 8 }} />
              <Typography style={{ fontSize: '0.82rem' }}>
                {pt ? 'Sem registos de manutenção' : 'No maintenance records'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default MaintenanceDialog;
